import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Card, CardBody, Row, Col } from 'reactstrap';
import { get, set, isEqual, camelCase, keys, startCase } from 'lodash';
import { format } from 'date-fns';
import { Chart, CardHeader, PseudoStates, Table, Field } from '@cordata/buffet';
import { Formik, Form } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { authorizedInstance as axios } from '~/utils';

const COLUMN_DELIMITER = ',';
const LINE_DELIMITER = '\n';

const isHidden = k => k.match(/^[a-zA-Z]+Uid$/) || k.match(/^isActive$/);

const filtersToQueryString = (filters, selectedFilters) => {
  const newParams = {};

  filters.forEach((f) => {
    const k = f.name;
    const gov = f.getOptionValue;

    let v = get(selectedFilters, k);
    v = v !== '' ? v : undefined;

    if (Array.isArray(v) && v.length) {
      if (gov) {
        v = v.map(gov);
      } else {
        v = v.map(vv => vv.value);
      }

      v = v.join(',');
    } else if (v instanceof Date) {
      v = format(v, 'MM/DD/YYYY');
    } else if (v && gov) {
      v = gov(v);
    } else if (v && v.value) {
      v = v.value;
    }

    set(newParams, k, v);
  });

  return newParams;
};

const Report = ({
  viewName,
  title,
  filters,
  hasChart,
  chartifyData,
  chartProps,
  ...props
}) => {
  const [data, setData] = useState([]);
  const [status, setStatus] = useState('loading');
  const [params, setParams] = useState({});
  const [oldParams, setOldParams] = useState(params);
  const [selectedFilters, setSelectedFilters] = useState({});

  const fetchData = () => {
    setStatus('loading');

    axios
      .get(`reports/${viewName}`, { params })
      .then((r) => {
        setData(r.data);
        setStatus('success');
      })
      .catch((e) => {
        console.error('error:', e);
        setStatus('error');
      });
  };

  const handleExport = () => {
    const columns = keys(data[0]).filter(r => !isHidden(r));

    const dataRows = data.map((item) => {
      const dataItemArray = columns.map((key) => {
        let value = item[key];

        if (value === null || value === undefined) {
          value = '';
        }

        return `"${value}"`;
      });
      return dataItemArray.join(COLUMN_DELIMITER);
    }).join(LINE_DELIMITER);

    const exportData = columns
      .map(c =>
        startCase(c))
      .join(COLUMN_DELIMITER) + LINE_DELIMITER + dataRows;

    let filename = (title || 'export').toString();

    if (!['.csv', '.txt'].includes(filename.slice(filename.lastIndexOf('.')))) {
      filename = `${filename}.csv`;
    }

    const link = document.createElement('a');

    link.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(exportData)}`);
    link.setAttribute('download', filename);
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const newParams = filtersToQueryString(filters, selectedFilters);

    setOldParams(params);

    setParams(newParams);
  }, [selectedFilters]);

  return (
    <Card>
      <CardHeader
        color="primary"
        inverse
        action={
          <Button
            outline
            color="light"
            size="sm"
            onClick={handleExport}
          >
            <FontAwesomeIcon icon="file-export" />
            <span className="d-none d-md-inline ml-2">Export</span>
          </Button>
        }
      >
        {title}
      </CardHeader>

      <CardBody>
        {status === 'success' ? (<>
          {hasChart && (<>
            <Chart
              data={chartifyData(data)}
              {...chartProps}
            />
          </>)}

          {filters.length > 0 && (
            <Formik
              initialValues={selectedFilters}
              validate={setSelectedFilters}
              validateOnBlur={false}
              onSubmit={fetchData}
            >
              <Form>
                <Row form>
                  {filters.map(f => (
                    <Col key={f.name}>
                      <Field {...f} />
                    </Col>
                  ))}
                </Row>

                <Row form>
                  <Col>
                    <Button type="submit" color="primary" className="float-right mb-3" disabled={isEqual(params, oldParams)}>
                      <FontAwesomeIcon icon="filter" /> Filter
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Formik>
          )}

          <Table
            columns={keys(data[0]).map(k => ({
              accessor: camelCase(k),
              Header: startCase(k),
              show: !isHidden(k),
              Cell: v =>
                (v.value && v.value.toString().match(/\d{1,2}-\d{1,2}-\d{2,4}/) ? (
                  <span>{format(v.value, 'MM-DD-YYYY')}</span>
                ) : (
                  <span>{v.value}</span>
                )),
            }))}
            data={data}
            {...props}
          />
        </>) : (
          <PseudoStates data={data} isLoading={status === 'loading'} error={status === 'error'} />
        )}
      </CardBody>
    </Card>
  );
};

Report.propTypes = {
  viewName: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  hasChart: PropTypes.bool,
  chartifyData: PropTypes.func,
  chartProps: PropTypes.shape(),
  filters: PropTypes.arrayOf(PropTypes.shape()),
};

Report.defaultProps = {
  hasChart: false,
  chartifyData: undefined,
  chartProps: undefined,
  filters: [],
};

export default Report;
